import { useCallback, useEffect, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { useActivateApplicationUser } from '../hooks/useActivateApplicationUser'
import { getApplicationToken } from '../hooks/useApplicationAuth'
import { useChangePassword } from '../hooks/useChangePassword'
import { useModalApplication } from '../hooks/useModalApplication'
import { useResetApplicationPassword } from '../hooks/useResetApplicationPassword'
import { useUpsertApplicationAccountUser } from '../hooks/useUpsertApplicationAccountUser'
import { useValidateApplicationAccountUser } from '../hooks/useValidateApplicationAccountUser'
import {
  AuthResult,
  UserDataWithDetails,
} from '../models/applicationConnection'
import { PasswordsStep } from './ConnectionSteps/PasswordsStep'
import { ResultStep } from './ConnectionSteps/ResultStep'
import { TermsStep } from './ConnectionSteps/TermsStep'
import { UserDataStep } from './ConnectionSteps/UserDataStep'

type FlowState = 'userdata' | 'svsPassword' | 'appPassword' | 'terms' | 'result'

export function useAppConnectionManager() {
  const { t } = useTranslation('common')
  const { handleModal, updateModal, resetModal } = useModalApplication()
  const { handleValidateApplicationAccountUser } =
    useValidateApplicationAccountUser()
  const { handleUpsertApplicationAccountUser } =
    useUpsertApplicationAccountUser()
  const { handleActivateApplicationUser } = useActivateApplicationUser()
  const { handleChangePassword } = useChangePassword()
  const { handleResetApplicationPassword } = useResetApplicationPassword()

  const [currentState, setCurrentState] = useState<FlowState>('userdata')
  const [userData, setUserData] = useState<UserDataWithDetails | null>(null)
  const [authResult, setAuthResult] = useState<AuthResult>({
    success: false,
    loading: false,
  })
  const [authError, setAuthError] = useState<boolean>(false)
  const [friendlyMessage, setFriendlyMessage] = useState<string | null>(null)
  const [errorSource, setErrorSource] = useState<FlowState | null>(null)
  const [appData, setAppData] = useState<{
    applicationId: string
    applicationName: string
    userId: string
    accountName?: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [svsPasswordValid, setSvsPasswordValid] = useState(false)
  const [appPasswordValid, setAppPasswordValid] = useState(false)
  const [upsertResponse, setUpsertResponse] = useState<{
    verifyToken?: string
    id: string
    isNewUser?: boolean
  } | null>(null)
  const [activateResponse, setActivateResponse] = useState<{
    verifyToken: string
    id: string
  } | null>(null)
  const svsPasswordRef = useRef<string>('')
  const appPasswordRef = useRef<string>('')

  const renderStep = () => {
    if (!appData) return null

    const stepNumber =
      currentState === 'userdata'
        ? 1
        : currentState === 'svsPassword'
          ? 2
          : currentState === 'appPassword'
            ? 3
            : currentState === 'terms'
              ? 2
              : 4

    const totalSteps = activateResponse ? 3 : 4

    switch (currentState) {
      case 'userdata':
        return (
          <UserDataStep
            appData={appData}
            userData={userData}
            error={error}
            stepInfo={{ current: stepNumber, total: totalSteps }}
          />
        )
      case 'svsPassword':
        return (
          <PasswordsStep
            key="svsPassword"
            appData={appData}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            onValidationChange={setSvsPasswordValid}
            hasActivationFlow={true}
            customDescription={t(
              'app_connection.steps.service_solutions_description',
            )}
            onFormChange={(values) => {
              const formValues = values as { newPassword?: string }
              svsPasswordRef.current = formValues.newPassword || ''
            }}
          />
        )
      case 'appPassword':
        return (
          <PasswordsStep
            key="appPassword"
            appData={appData}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            onValidationChange={setAppPasswordValid}
            hasActivationFlow={true}
            customDescription={t(
              'app_connection.steps.application_description',
              {
                applicationName: appData.applicationName,
                accountName: appData.accountName
                  ? ` (${appData.accountName})`
                  : '',
              },
            )}
            customLabel={t(
              'app_connection.passwords.existing_user.application_label',
              {
                applicationName: appData.applicationName,
              },
            )}
            customPlaceholder={t(
              'app_connection.passwords.existing_user.application_placeholder',
              {
                applicationName: appData.applicationName,
              },
            )}
            customInfoMessage={t(
              'app_connection.passwords.existing_user.warning_message',
              {
                applicationName: appData.applicationName,
              },
            )}
            onFormChange={(values) => {
              const formValues = values as { newPassword?: string }
              appPasswordRef.current = formValues.newPassword || ''
            }}
          />
        )
      case 'terms':
        return (
          <TermsStep
            appData={appData}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            onFormChange={setTermsAccepted}
          />
        )
      case 'result':
        return (
          <ResultStep
            appData={appData}
            authResult={authResult}
            stepInfo={{ current: stepNumber, total: totalSteps }}
            authError={authError}
            friendlyMessage={friendlyMessage}
          />
        )
      default:
        return (
          <UserDataStep
            appData={appData}
            userData={userData}
            error={error}
            stepInfo={{ current: stepNumber, total: totalSteps }}
          />
        )
    }
  }

  const handleNext = useCallback(async () => {
    if (!appData) return

    switch (currentState) {
      case 'userdata':
        if (error) {
          setCurrentState('userdata')
          setUserData(null)
          setAuthResult({ success: false, loading: false })
          setAppData(null)
          setError(null)
          setTermsAccepted(false)
          resetModal()
          return
        }

        if (userData) {
          try {
            const upsertResult = await handleUpsertApplicationAccountUser({
              applicationId: userData.applicationId,
              userId: userData.userId,
              email: userData.email,
              displayName: userData.displayName,
              name: userData.name,
              documentNumber: userData.documentNumber,
              externalUserId: userData.externalUserId,
              role: userData.role,
              isNewUser: userData.isNewUser,
            })

            setUpsertResponse(upsertResult)

            if ('verifyToken' in upsertResult) {
              setCurrentState('terms')
              setTermsAccepted(false)
            } else if (
              'status' in upsertResult &&
              upsertResult.status === 'CONNECTED'
            ) {
              setAuthResult({ success: true, loading: false })
              setCurrentState('result')
              queryClient.refetchQueries({ queryKey: ['/user/get-user-info'] })
              queryClient.invalidateQueries({
                queryKey: ['/user/current-accounts'],
              })
              setTimeout(() => {
                resetModal()
              }, 3000)
            } else {
              setCurrentState('svsPassword')
            }
          } catch (error) {
            setAuthResult({ success: false, loading: false })
            setAuthError(true)
            setErrorSource('userdata')
            setCurrentState('result')
            if (error instanceof AxiosError) {
              const apiError = error.response?.data as ApiError
              setFriendlyMessage(apiError?.friendlyMessage || null)
            }
          }
        }
        break

      case 'svsPassword': {
        if (!svsPasswordValid || !svsPasswordRef.current) {
          return
        }

        try {
          await getApplicationToken({
            username: userData?.email || appData.userId,
            password: svsPasswordRef.current,
          })

          setSvsPasswordValid(false)
          setAppPasswordValid(false)
          setCurrentState('appPassword')
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          setErrorSource('svsPassword')
          setCurrentState('result')
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'appPassword': {
        if (!appPasswordValid || !appPasswordRef.current) {
          return
        }

        setAuthResult({ success: false, loading: true })
        setAuthError(false)
        setCurrentState('result')

        try {
          if (activateResponse && userData?.email) {
            const accessToken = await getApplicationToken({
              username: userData?.email,
              password: appPasswordRef.current,
            })

            await handleResetApplicationPassword(
              activateResponse.id,
              appPasswordRef.current,
              activateResponse.verifyToken,
              accessToken,
            )
          } else if (upsertResponse) {
            const accessToken = await getApplicationToken({
              username: userData?.email || appData.userId,
              password: svsPasswordRef.current,
            })

            await handleChangePassword(
              upsertResponse.id,
              {
                currentPassword: appPasswordRef.current,
                newPassword: svsPasswordRef.current,
              },
              accessToken,
            )
          }

          setAuthResult({ success: true, loading: false })
          queryClient.refetchQueries({ queryKey: ['/user/get-user-info'] })
          queryClient.invalidateQueries({
            queryKey: ['/user/current-accounts'],
          })

          setTimeout(() => {
            resetModal()
          }, 3000)
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          setErrorSource('appPassword')
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'terms': {
        if (!termsAccepted || !upsertResponse?.verifyToken) {
          return
        }

        try {
          const result = await handleActivateApplicationUser(
            upsertResponse.id,
            upsertResponse.verifyToken,
          )
          setActivateResponse(result)
          setCurrentState('svsPassword')
        } catch {
          resetModal()
        }
        break
      }

      case 'result': {
        setCurrentState('userdata')
        setUserData(null)
        setAuthResult({ success: false, loading: false })
        setAppData(null)
        setTermsAccepted(false)
        setFriendlyMessage(null)
        setErrorSource(null)
        setAuthError(false)
        resetModal()
        return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appData,
    currentState,
    error,
    userData,
    handleUpsertApplicationAccountUser,
    handleActivateApplicationUser,
    handleChangePassword,
    handleResetApplicationPassword,
    handleValidateApplicationAccountUser,
    svsPasswordValid,
    appPasswordValid,
    termsAccepted,
    upsertResponse,
    activateResponse,
    resetModal,
    authError,
    errorSource,
  ])

  const handleConnectApplication = async (
    applicationId: string,
    userId: string,
    applicationName: string,
    accountName?: string,
  ) => {
    setAppData({ applicationId, applicationName, userId, accountName })
    setCurrentState('userdata')

    handleModal({
      title: t('app_connection.modal.title'),
      description: renderStep(),
      showButtons: false,
      onConfirm: handleNext,
    })

    try {
      const result = await handleValidateApplicationAccountUser({
        applicationId,
        userId,
      })
      setUserData(result)
    } catch {
      resetModal()
    }
  }

  const getCancelButtonText = useCallback(() => {
    if (currentState === 'result' && authError)
      return t('app_connection.buttons.back')
    if (
      currentState === 'svsPassword' ||
      currentState === 'appPassword' ||
      currentState === 'terms'
    )
      return t('app_connection.buttons.back')
    return undefined
  }, [currentState, authError, t])

  const handleBack = useCallback(async () => {
    if (currentState === 'svsPassword') {
      setCurrentState('userdata')
      setSvsPasswordValid(false)
      setUserData(null)
      setError(null)

      if (appData) {
        try {
          const result = await handleValidateApplicationAccountUser({
            applicationId: appData.applicationId,
            userId: appData.userId,
          })
          setUserData(result)
        } catch {
          resetModal()
        }
      }
    } else if (currentState === 'appPassword') {
      setCurrentState('svsPassword')
      setAppPasswordValid(false)
    } else if (currentState === 'terms') {
      setCurrentState('userdata')
      setUserData(null)
      setError(null)

      if (appData) {
        try {
          const result = await handleValidateApplicationAccountUser({
            applicationId: appData.applicationId,
            userId: appData.userId,
          })
          setUserData(result)
        } catch {
          resetModal()
        }
      }
    } else if (currentState === 'result' && authError) {
      if (errorSource === 'userdata') {
        setCurrentState('userdata')
      } else if (errorSource === 'svsPassword') {
        setCurrentState('svsPassword')
      } else if (errorSource === 'appPassword') {
        setCurrentState('appPassword')
      }
      setAuthError(false)
      setAuthResult({ success: false, loading: false })
      setFriendlyMessage(null)
      setErrorSource(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState, authError, errorSource])

  useEffect(() => {
    if (!appData) return

    const getButtonText = () => {
      if (currentState === 'userdata') {
        return error
          ? t('app_connection.buttons.close')
          : t('app_connection.buttons.continue')
      }
      if (currentState === 'svsPassword' || currentState === 'appPassword')
        return t('app_connection.buttons.authenticate')
      if (currentState === 'terms') return t('app_connection.buttons.continue')
      if (currentState === 'result' && authError)
        return t('app_connection.buttons.close')
      return t('app_connection.buttons.close')
    }

    const shouldShowButtons =
      currentState === 'userdata'
        ? !!userData || !!error
        : currentState === 'result'
          ? !authResult.loading
          : true

    const isDisabled =
      currentState === 'terms'
        ? !termsAccepted
        : currentState === 'svsPassword'
          ? !svsPasswordValid
          : currentState === 'appPassword'
            ? !appPasswordValid
            : false

    updateModal({
      title: t('app_connection.modal.title'),
      description: renderStep(),
      confirmText: getButtonText(),
      cancelText: getCancelButtonText(),
      showButtons: shouldShowButtons,
      onConfirm: handleNext,
      onCancel: getCancelButtonText() ? handleBack : undefined,
      disabled: isDisabled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentState,
    userData,
    authResult,
    appData,
    error,
    termsAccepted,
    authError,
    svsPasswordValid,
    appPasswordValid,
    getCancelButtonText,
    handleBack,
  ])

  return {
    handleConnectApplication,
  }
}
